import { useAction, useAtom } from '@reatom/npm-react'
import { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { SegmentControl, SegmentControlScrollable } from '@blade/ui-kit'
import { USER_TRADING_ANALYTICS_PERIOD_LABEL_MAP } from '@root/features/user/user-trading-analytics/models'
import { Drawer } from '@root/shared/ui-kit'

import { distributeRangeAtom, reatomDistributeTable } from '../atoms'
import { DISTRIBUTE_RANGES, distributeRangeId } from '../model'

import { DistributeTable } from './distribute-table/distribute-table'

import type { DelugeId } from '@root/entities/user-short'
import type { UserTradingAnalyticsPeriod } from '@root/features/user/user-trading-analytics/models'
import type { ClassNameProps } from '@root/shared/react'

type DistributeDrawerProps = {
  period: UserTradingAnalyticsPeriod
  delugeId: DelugeId
} & ClassNameProps

const ROW_VARIANT = 'stacked'

export const DistributeDrawer: React.FC<DistributeDrawerProps> = ({
  period,
  delugeId,
  className,
}) => {
  const { t } = useTranslation()
  const [distributeRange, setDistributeRange] = useAtom(distributeRangeAtom)

  const model = useMemo(
    // eslint-disable-next-line @reatom/unit-naming-rule
    () => reatomDistributeTable(delugeId, ROW_VARIANT),
    [delugeId],
  )
  const [, setPeriod] = useAtom(model.periodAtom)
  const load = useAction(model.tableModel.load)

  useEffect(() => {
    setPeriod(period)
  }, [period, setPeriod])

  useEffect(() => {
    if (!distributeRange) {
      return
    }

    load()
  }, [distributeRange, period, load])

  const handleClose = () => setDistributeRange(undefined)
  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setDistributeRange(undefined)
    }
  }

  const title = (
    <>
      {t(USER_TRADING_ANALYTICS_PERIOD_LABEL_MAP[period])}{' '}
      {t('profile.distribute-drawer.token-distribution')}
    </>
  )

  return (
    <Root
      title={title}
      open={distributeRange !== undefined}
      className={className}
      onOpenChange={handleOpenChange}
    >
      <Content>
        <Padded>
          <SegmentControlScrollable
            size="h24"
            getId={distributeRangeId}
            value={distributeRange ?? [-Infinity, Infinity]}
            onChange={setDistributeRange}
          >
            {DISTRIBUTE_RANGES.map(([range, label]) => (
              <SegmentControl.Item key={distributeRangeId(range)} value={range}>
                {label === 'ALL' ? t('common.all') : label}
              </SegmentControl.Item>
            ))}
          </SegmentControlScrollable>
        </Padded>

        <DistributeTable
          model={model}
          rowVariant={ROW_VARIANT}
          onClose={handleClose}
        />
      </Content>
    </Root>
  )
}

/** @deprecated Use DistributeDrawer */
export const DistributeDrawerMobile = DistributeDrawer

const Root = styled(Drawer)`
  top: var(--safe-top);
  bottom: 0;
  height: auto;
  max-height: none;
  background: var(--background-1);
`

const Content = styled.div`
  display: grid;
  grid-template-rows: auto minmax(0, 1fr);
  gap: 16px;
  height: 100%;
  min-height: 0;
  padding-block: 6px 0;
`

const Padded = styled.div`
  padding-inline: 12px;
`
